import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import MarqueeBand from "@/components/MarqueeBand";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { openBooking, submitLead } from "@/lib/api";

const intents = [
  { key: "audit", label: "Free Marketing Audit" },
  { key: "quote", label: "Request a Quote" },
  { key: "contact", label: "Just Say Aloha" },
];

const initial = { name: "", email: "", phone: "", company: "", website: "", service: "", message: "" };

const ContactForm = () => {
  const [intent, setIntent] = useState("audit");
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitLead({ ...form, type: intent });
      toast.success("Received. The colony is on it — expect a reply within one business day.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="card-brutal shadow-[8px_8px_0_0_#0A0A0A] p-6 md:p-10" data-testid="lead-form">
      <p className="kicker mb-4">I want to…</p>
      <div className="flex flex-wrap gap-3 mb-8" data-testid="intent-selector">
        {intents.map((i) => (
          <button
            key={i.key}
            type="button"
            onClick={() => setIntent(i.key)}
            data-testid={`intent-${i.key}-button`}
            className={`font-mono text-xs font-bold uppercase tracking-widest border-2 border-black px-4 py-2.5 transition-colors duration-200 ${
              intent === i.key ? "bg-black text-lime" : "bg-white hover:bg-lime"
            }`}
          >
            {i.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="label-mono" htmlFor="lf-name">Name *</label>
          <input id="lf-name" data-testid="lead-name-input" required className="input-brutal" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
        </div>
        <div>
          <label className="label-mono" htmlFor="lf-email">Email *</label>
          <input id="lf-email" data-testid="lead-email-input" required type="email" className="input-brutal" value={form.email} onChange={set("email")} placeholder="jane@company.com" />
        </div>
        <div>
          <label className="label-mono" htmlFor="lf-phone">Phone</label>
          <input id="lf-phone" data-testid="lead-phone-input" className="input-brutal" value={form.phone} onChange={set("phone")} placeholder="(808) 555-0000" />
        </div>
        <div>
          <label className="label-mono" htmlFor="lf-company">Company</label>
          <input id="lf-company" data-testid="lead-company-input" className="input-brutal" value={form.company} onChange={set("company")} placeholder="Company LLC" />
        </div>
        {intent !== "contact" && (
          <>
            <div>
              <label className="label-mono" htmlFor="lf-website">Website</label>
              <input id="lf-website" data-testid="lead-website-input" className="input-brutal" value={form.website} onChange={set("website")} placeholder="yoursite.com" />
            </div>
            <div>
              <label className="label-mono" htmlFor="lf-service">Service of interest</label>
              <select id="lf-service" data-testid="lead-service-select" className="input-brutal" value={form.service} onChange={set("service")}>
                <option value="">Not sure yet</option>
                <option>SEO</option>
                <option>Social Media Marketing</option>
                <option>Paid Advertising</option>
                <option>Web Design & Development</option>
                <option>Lead Generation</option>
                <option>Full-service / Everything</option>
              </select>
            </div>
          </>
        )}
        <div className="md:col-span-2">
          <label className="label-mono" htmlFor="lf-message">Tell us about your goals</label>
          <textarea id="lf-message" data-testid="lead-message-input" rows={4} className="input-brutal resize-none" value={form.message} onChange={set("message")} placeholder="What are you trying to grow, and what's standing in the way?" />
        </div>
      </div>
      <button type="submit" disabled={sending} data-testid="lead-submit-button" className="btn-brutal-lime w-full mt-8 disabled:opacity-60">
        {sending ? "Sending..." : `Send — ${intents.find((i) => i.key === intent).label}`} <ArrowUpRight size={16} />
      </button>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        No spam, no pressure. One business-day reply, guaranteed.
      </p>
    </form>
  );
};

export default function Contact() {
  return (
    <>
      <section className="px-5 md:px-10 pt-16 md:pt-24 pb-12 border-b-2 border-black" data-testid="contact-hero">
        <p className="kicker mb-6" data-testid="contact-kicker">
          <span className="bg-black text-lime px-2 py-1">Contact</span>
        </p>
        <h1 className="font-display uppercase text-[13vw] md:text-[9vw] leading-[0.85]" data-testid="contact-heading">
          <MaskedLine delay={0.1}>Talk to</MaskedLine>
          <MaskedLine delay={0.25}>
            the <span className="bg-lime px-2 box-decoration-clone">colony.</span>
          </MaskedLine>
        </h1>
      </section>

      <MarqueeBand items={["Free Audit", "No Contracts", "Same-Day Answers", "Aloha"]} />

      <section className="px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="contact-body">
        <div className="lg:col-span-5">
          <FadeUp>
            <h2 className="font-display text-3xl md:text-5xl uppercase leading-[0.9] mb-8">
              Three ways to start marching
            </h2>
          </FadeUp>
          <div className="space-y-5">
            <FadeUp delay={0.05}>
              <a href="tel:+17723324038" data-testid="contact-call-card" className="card-brutal flex items-center gap-5 p-6 shadow-[6px_6px_0_0_#CCFF00] hover:shadow-[6px_6px_0_0_#0A0A0A] hover:-translate-y-1 transition-[transform,box-shadow] duration-200">
                <span className="w-12 h-12 bg-black text-lime flex items-center justify-center border-2 border-black shrink-0"><Phone size={22} /></span>
                <span>
                  <span className="label-mono mb-1">Call the crew</span>
                  <span className="font-display text-2xl md:text-3xl">772-332-4038</span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Placeholder — swap in your line</span>
                </span>
              </a>
            </FadeUp>
            <FadeUp delay={0.1}>
              <a href="mailto:ants@antcrawler.com" data-testid="contact-email-card" className="card-brutal flex items-center gap-5 p-6 shadow-[6px_6px_0_0_#CCFF00] hover:shadow-[6px_6px_0_0_#0A0A0A] hover:-translate-y-1 transition-[transform,box-shadow] duration-200">
                <span className="w-12 h-12 bg-black text-lime flex items-center justify-center border-2 border-black shrink-0"><Mail size={22} /></span>
                <span>
                  <span className="label-mono mb-1">Email</span>
                  <span className="font-display text-2xl md:text-3xl break-all">ants@antcrawler.com</span>
                </span>
              </a>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="card-brutal flex items-center gap-5 p-6" data-testid="contact-address-card">
                <span className="w-12 h-12 bg-black text-lime flex items-center justify-center border-2 border-black shrink-0"><MapPin size={22} /></span>
                <span>
                  <span className="label-mono mb-1">The nest</span>
                  <span className="font-display text-2xl md:text-3xl leading-tight">131 N. 2nd St. Suite 216</span>
                  <span className="block font-mono text-xs uppercase tracking-widest mt-1">Fort Pierce, Florida</span>
                </span>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="card-brutal flex items-center gap-5 p-6" data-testid="contact-hours-card">
                <span className="w-12 h-12 bg-lime flex items-center justify-center border-2 border-black shrink-0"><Clock size={22} /></span>
                <span>
                  <span className="label-mono mb-1">Hours</span>
                  <span className="font-display text-2xl md:text-3xl">Mon–Fri, 9–5 HST</span>
                </span>
              </div>
            </FadeUp>
            <FadeUp delay={0.25}>
              <button onClick={openBooking} data-testid="contact-book-button" className="btn-brutal w-full">
                Or book a consultation slot <ArrowUpRight size={16} />
              </button>
            </FadeUp>
          </div>
        </div>
        <div className="lg:col-span-7">
          <FadeUp delay={0.1}>
            <ContactForm />
          </FadeUp>
        </div>
      </section>

      <section className="border-t-2 border-black" data-testid="map-section">
        <div className="px-5 md:px-10 pt-14 pb-8">
          <FadeUp>
            <p className="kicker mb-3">Find the nest</p>
            <h2 className="font-display text-3xl md:text-5xl uppercase leading-[0.95]" data-testid="map-address-heading">
              131 N. 2nd St. Suite 216, Fort Pierce, FL
            </h2>
          </FadeUp>
        </div>
        <iframe
          title="AntCrawler location — 131 N. 2nd St. Suite 216, Fort Pierce, FL"
          data-testid="contact-map"
          src="https://maps.google.com/maps?q=131%20N%202nd%20St%20Suite%20216%2C%20Fort%20Pierce%2C%20FL&t=&z=16&ie=UTF8&iwloc=&output=embed"
          className="w-full h-[380px] md:h-[460px] border-t-2 border-black grayscale contrast-125"
          loading="lazy"
        />
      </section>
    </>
  );
}
